import { type Dayjs } from 'dayjs';
import { type ReactElement, useMemo } from 'react';
import { resolveStartedAtFilterBounds } from '@/features/jobs/components/jobsTableDateFilters';
import { JobsTableV2 } from '@/features/jobs/components/JobsTableV2';
import { queryJobs } from '@/features/jobs/data/jobs';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { useDateWindow, type DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { queryKeys } from '@/shared/hooks';
import { Stack } from '@/ui';
import {
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { TagOption } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { BuildStatus, JobListItemDto, QueryResult } from '@flowqa/shared';

export type JobSortKey =
  | 'pipelineName'
  | 'stageName'
  | 'jobName'
  | 'status'
  | 'runTotal'
  | 'runFrequencyPerDay'
  | 'runAverageDurationMs'
  | 'runPassRate'
  | 'runFlakeScore'
  | 'startedAt';

const jobSortFieldMap: Record<JobSortKey, string> = {
  pipelineName: 'pipelineName',
  stageName: 'stageName',
  jobName: 'jobName',
  status: 'status',
  runTotal: 'runTotal',
  runFrequencyPerDay: 'runFrequencyPerDay',
  runAverageDurationMs: 'runAverageDurationMs',
  runPassRate: 'runPassRate',
  runFlakeScore: 'runFlakeScore',
  startedAt: 'startedAt',
};

export type JobsTablePanelProps = {
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  tagOptions: TagOption[];
  dateQuickFilter: DateQuickFilter;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  latestQueryStartDay: string | null;
  latestQueryEndDay: string | null;
  projectOptions?: Array<{ value: string; label: string }>;
  jobSearchValue: string;
  jobSortKey: JobSortKey;
  jobSortDirection: 'asc' | 'desc';
  onJobSortChange: (nextSortKey: JobSortKey, nextSortDirection: 'asc' | 'desc') => void;
  filterStatuses: BuildStatus[];
  filterPipelineIds: string[];
  filterStageNames: string[];
  filterJobNames: string[];
  filterTags: string[];
};

export const JobsTablePanel = ({
  selectedProjectId,
  selectedOrganisationId,
  tagOptions,
  dateQuickFilter,
  fromDate,
  toDate,
  latestQueryStartDay,
  latestQueryEndDay,
  projectOptions = [],
  jobSearchValue,
  jobSortKey,
  jobSortDirection,
  onJobSortChange,
  filterStatuses,
  filterPipelineIds,
  filterStageNames,
  filterJobNames,
  filterTags,
}: JobsTablePanelProps): ReactElement => {
  const dateWindow = useDateWindow({
    dateQuickFilter,
    fromDate,
    toDate,
    latestQueryStartDay,
    latestQueryEndDay,
  });
  const startedAtFilterBounds = useMemo(
    () =>
      resolveStartedAtFilterBounds({
        startDay: dateWindow.startDay,
        endDay: dateWindow.endDay,
      }),
    [dateWindow.endDay, dateWindow.startDay],
  );
  const statusFilterKey = useMemo(
    () => [...filterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [filterStatuses],
  );
  const tagFilterKey = useMemo(
    () => [...filterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [filterTags],
  );
  const pipelineFilterKey = useMemo(
    () => [...filterPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
    [filterPipelineIds],
  );
  const stageFilterKey = useMemo(
    () => [...filterStageNames].sort((left, right) => left.localeCompare(right)).join(','),
    [filterStageNames],
  );

  return (
    <Stack gap="xl">
      <JobsTableV2
        queryFn={async (input: GridQueryInput): Promise<QueryResult<JobListItemDto>> =>
          await queryJobs(selectedProjectId, selectedOrganisationId ?? undefined, {
            ...input,
            search: jobSearchValue,
            sorts: [{ field: jobSortFieldMap[jobSortKey], direction: jobSortDirection }],
            filters: mergeFilters(
              buildInFilter('status', filterStatuses),
              buildInFilter('pipelineId', filterPipelineIds),
              buildInFilter('stageName', filterStageNames),
              buildInFilter('jobName', filterJobNames),
              buildInFilter('tags', filterTags),
              buildDateRangeFilters(
                'startedAt',
                startedAtFilterBounds.startAt,
                startedAtFilterBounds.endAt,
              ),
            ),
          })
        }
        queryKey={queryKeys.dataGrid(
          'jobs/table',
          selectedOrganisationId ?? null,
          selectedProjectId ?? null,
          jobSearchValue,
          jobSortKey,
          jobSortDirection,
          statusFilterKey,
          pipelineFilterKey,
          stageFilterKey,
          [...filterJobNames].sort((left, right) => left.localeCompare(right)).join(','),
          tagFilterKey,
          dateQuickFilter,
          startedAtFilterBounds.startAt,
          startedAtFilterBounds.endAt,
        )}
        projectOptions={projectOptions}
        jobRunsEndAt={startedAtFilterBounds.endAt}
        jobRunsStartAt={startedAtFilterBounds.startAt}
        noRowsText={formatRangeWindowNoDataText(
          getEmptyStateMessage('entity.noneFound', { entity: 'job' }),
          dateQuickFilter,
          false,
        )}
        showProjectColumn={selectedProjectId === null}
        sort={{ field: jobSortKey, direction: jobSortDirection }}
        onSortChange={(nextSort) => {
          onJobSortChange(nextSort.field as JobSortKey, nextSort.direction);
        }}
        tagOptionsOverride={tagOptions}
      />
    </Stack>
  );
};
